import { useState, useEffect, useRef, useMemo } from 'react';
import { PencilSimpleIcon, TrashIcon, CheckIcon } from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { format } from 'date-fns';
import type { Transaction } from '@/api/rpc/hooks/ship-and-debit/useE844sdTransactions';
import { isPostedFrozen } from '@/api/rpc/hooks/ship-and-debit/useE844sdTransactions';
import { formatAccountingCurrency, formatNumber } from '@/utils/formatters';
import type { TransactionNavigationInfo } from './TransactionViewModal';
import { SpaDunsWarning } from '../../spa-duns/SpaDunsWarning';
import type { SpaDunsComparison } from '../../spa-duns/spaDunsComparison';
import { T, Var, useGT } from 'gt-react';

// ── Helpers ────────────────────────────────────────────────────────────

function tx(transaction: Transaction, field: string): unknown {
  return (transaction as unknown as Record<string, unknown>)[field];
}

function txStr(transaction: Transaction, field: string): string {
  return String(tx(transaction, field) ?? '') || '';
}

function fmtDate(value: unknown, style: 'short' | 'long' = 'short'): string {
  const str = String(value ?? '');
  if (!/^\d{8}$/.test(str)) return '—';
  const year = Number(str.substring(0, 4));
  const month = Number(str.substring(4, 6));
  const day = Number(str.substring(6, 8));
  const d = new Date(year, month - 1, day);
  if (
    Number.isNaN(d.getTime()) ||
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return '—';
  }
  return style === 'short' ? format(d, 'MMM d') : format(d, 'MMM d, yyyy');
}

/** INVMAS stores dates as 6-digit YYMMDD integers (e.g. 260317 → Mar 17, 2026) */
function fmtInvDate(value: unknown): string {
  const v = Number(value);
  if (!v || !Number.isFinite(v)) return '—';
  const year = 2000 + Math.floor(v / 10000);
  const month = Math.floor((v % 10000) / 100);
  const day = v % 100;
  const d = new Date(year, month - 1, day);
  if (
    Number.isNaN(d.getTime()) ||
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return '—';
  }
  return `${format(d, 'MMM d')}, '${format(d, 'yy')}`;
}

// ── Types ──────────────────────────────────────────────────────────────

type TabId = 'item' | 'customer' | 'pricing' | 'raw';

// API field keys for buyer cost rows in the pricing tab (not user-visible text)
const BUYER_COST_FIELD_KEYS = [
  'inv_time_branch_buyer_cost',
  'invoice_time_buyer_cost',
  'order_time_branch_buyer_cost',
  'order_time_buyer_cost',
] as const;

// Date-fns format string for activity timestamps (not user-visible pattern)
const ACTIVITY_TIMESTAMP_FORMAT = 'MMM d, yyyy · h:mm a';

// Fallback CSS class for activity badge (not user-visible string)
const DEFAULT_BADGE_COLOR = 'bg-gray-100 text-gray-700';

const TABS: { id: TabId; label: string; key: string }[] = [
  { id: 'item', label: 'Item', key: '1' },
  { id: 'customer', label: 'Customer', key: '2' },
  { id: 'pricing', label: 'Pricing', key: '3' },
  { id: 'raw', label: 'Raw data', key: '4' },
];

interface Props {
  transaction: Transaction;
  fullTransactionData: Record<string, unknown> | undefined;
  isLoadingDetails: boolean;
  navigationInfo?: TransactionNavigationInfo;
  locationLookup: {
    companyName: string;
    divisionName: string;
    branchName: string;
  };
  onAddToWorkbench?: (transaction: Transaction) => void;
  onStageInternal?: (transaction: Transaction) => void;
  /** True while a parent mutation is in flight; disables Stage / Stage
   *  Internal CTAs and the keyboard "s" shortcut. */
  isActionPending?: boolean;
  onAddComment: (text: string) => void;
  onEditComment?: (activityId: string, text: string) => Promise<void>;
  onDeleteComment?: (activityId: string) => void;
  isDeletePending?: boolean;
  currentUser?: { id?: string; isAdmin?: boolean; name?: string } | null;
  /** The DUNS is not on any SPA active on the invoice date; shown beside it. */
  dunsMismatch?: SpaDunsComparison | null;
}

// ── Activity helpers ───────────────────────────────────────────────────

const ACTIVITY_LABELS: Record<string, string> = {
  field_modified: 'Modified',
  pricing_recalculated: 'Recalculated',
  staged: 'Staged',
  unstaged: 'Unstaged',
  staged_internal: 'Staged Internal',
  unstaged_internal: 'Unstaged Internal',
  sent_via_edi: 'Sent (EDI)',
  sent_via_email: 'Sent (Email)',
  comment_added: 'Comment',
  converted_from_e844sd: 'Imported',
  classified_internal: 'Classified',
  bpid_refreshed: 'BPID Refreshed',
  field_modified_by_rule: 'Rule Applied',
};

const SYSTEM_ACTIVITY_TYPES = new Set([
  'converted_from_e844sd',
  'classified_internal',
  'pricing_recalculated',
  'bpid_refreshed',
  'field_modified_by_rule',
]);

const ACTIVITY_BADGE_COLORS: Record<string, string> = {
  converted_from_e844sd: 'bg-purple-100 text-purple-700',
  classified_internal: 'bg-purple-100 text-purple-700',
  pricing_recalculated: 'bg-purple-100 text-purple-700',
  bpid_refreshed: 'bg-purple-100 text-purple-700',
  field_modified_by_rule: 'bg-purple-100 text-purple-700',
  comment_added: 'bg-green-100 text-green-700',
  staged: 'bg-amber-100 text-amber-700',
  staged_internal: 'bg-amber-100 text-amber-700',
  unstaged: 'bg-amber-100 text-amber-700',
  unstaged_internal: 'bg-amber-100 text-amber-700',
  sent_via_edi: 'bg-blue-100 text-blue-700',
  sent_via_email: 'bg-blue-100 text-blue-700',
  field_modified: 'bg-gray-100 text-gray-700',
};

// ── Component ──────────────────────────────────────────────────────────

export function TransactionViewV2({
  transaction,
  fullTransactionData,
  isLoadingDetails,
  navigationInfo,
  locationLookup,
  onAddToWorkbench,
  onStageInternal,
  isActionPending = false,
  onAddComment,
  onEditComment,
  onDeleteComment,
  isDeletePending = false,
  currentUser,
  dunsMismatch = null,
}: Props) {
  const gt = useGT();
  // Posted (GL-posted) transactions are frozen — only batch-and-send is allowed.
  // Disable the Stage / Stage Internal CTAs and the "s" shortcut for them.
  const isPostedTx = isPostedFrozen(transaction);
  // Explain why the Stage controls are disabled (native title shows even on a
  // disabled button). Mirrors the v1 modal's Unstage tooltip.
  const postedFreezeTitle = isPostedTx
    ? gt(
        'This transaction has been posted (GL-posted) and is frozen — only batch-and-send is allowed.',
        {
          context:
            'tooltip explaining why Stage actions are disabled for a posted ship-and-debit transaction',
        },
      )
    : undefined;
  const [activeTab, setActiveTab] = useState<TabId>('item');
  const [commentText, setCommentText] = useState('');
  const [showAllRaw, setShowAllRaw] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [invoicePage, setInvoicePage] = useState(0);
  const INVOICE_PAGE_SIZE = 7;
  const commentRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset local state when transaction changes
  useEffect(() => {
    setActiveTab('item');
    setShowAllRaw(false);
    setInvoicePage(0);
    setCommentText('');
  }, [transaction.id]);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      )
        return;

      switch (e.key.toLowerCase()) {
        case 'j':
          navigationInfo?.onNavigate('next');
          break;
        case 'k':
          navigationInfo?.onNavigate('prev');
          break;
        case 's':
          if (onAddToWorkbench && !isActionPending && !isPostedTx)
            onAddToWorkbench(transaction);
          break;
        case 'c':
          e.preventDefault();
          commentRef.current?.focus();
          break;
        case '1':
          setActiveTab('item');
          break;
        case '2':
          setActiveTab('customer');
          break;
        case '3':
          setActiveTab('pricing');
          break;
        case '4':
          setActiveTab('raw');
          break;
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [
    navigationInfo,
    onAddToWorkbench,
    transaction,
    isActionPending,
    isPostedTx,
  ]);

  // Derived data
  const claimAmount = Number(tx(transaction, 'claim_amount')) || 0;
  const quotedPrice = Number(tx(transaction, 'unit_price_quoted')) || 0;
  const wholesalePrice = Number(tx(transaction, 'unit_price_wholesale')) || 0;
  const landedCost = Number(tx(transaction, 'landed_cost')) || 0;
  const spaCost = Number(
    (fullTransactionData?.e844d as Record<string, unknown> | undefined)?.[
      'unit_price_quoted'
    ] ?? 0,
  );
  const quantity = Number(tx(transaction, 'quantity')) || 0;
  const quantityUom = txStr(transaction, 'quantity_uom');
  const isNegativeQty = quantity < 0;
  const isNegativeClaim = claimAmount < 0;

  const invoiceLines = useMemo(
    () =>
      ((fullTransactionData?.invoiceMasterData as unknown[]) ?? []) as Record<
        string,
        unknown
      >[],
    [fullTransactionData?.invoiceMasterData],
  );

  const itemMaster = useMemo(
    () =>
      ((fullTransactionData?.itemMasterData as unknown[]) ?? [])[0] as
        Record<string, unknown> | undefined,
    [fullTransactionData?.itemMasterData],
  );

  const activities = useMemo(() => {
    const raw = (fullTransactionData?.activities ?? []) as Array<{
      id: string;
      activityType: string;
      description: string;
      eventAt: string;
      createdAt: string;
      userId: string | null;
      metadata: Record<string, unknown> | null;
      user?: { name?: string };
    }>;
    const isAdmin = currentUser?.isAdmin ?? false;
    return raw.filter(
      (a) => isAdmin || !SYSTEM_ACTIVITY_TYPES.has(a.activityType),
    );
  }, [fullTransactionData?.activities, currentUser?.isAdmin]);

  const rawFields = useMemo(() => {
    const record = transaction as unknown as Record<string, unknown>;
    return Object.entries(record)
      .filter(([, v]) => v != null && v !== '')
      .sort(([a], [b]) => a.localeCompare(b));
  }, [transaction]);

  const handleSubmitComment = () => {
    if (!commentText.trim()) return;
    onAddComment(commentText.trim());
    setCommentText('');
  };

  const companyCode = txStr(transaction, 'company');
  const divisionCode = txStr(transaction, 'division');

  return (
    <div ref={containerRef} className="flex flex-col h-full">
      {/* ── Header chips + actions ──────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 pb-3 border-b">
        <div className="flex items-center gap-2 flex-wrap">
          {(() => {
            // eslint-disable-next-line i18next/no-literal-string -- dynamic data values from transaction record, not translatable strings
            const supplierName = txStr(transaction, 'supplier_name');
            // eslint-disable-next-line i18next/no-literal-string -- dynamic data values from transaction record, not translatable strings
            const tradingPartner = txStr(transaction, 'trading_partner');
            const supplierDisplay = supplierName || tradingPartner;
            // eslint-disable-next-line i18next/no-literal-string -- dynamic data value from transaction record
            const shipType = txStr(transaction, 'ship_type');
            // eslint-disable-next-line i18next/no-literal-string -- dynamic data value from transaction record
            const rebateType = txStr(transaction, 'rebate_type');
            const divisionDisplay =
              (locationLookup.divisionName || divisionCode) +
              (companyCode ? ` (${companyCode})` : '');
            return (
              <>
                {supplierDisplay && (
                  <Badge variant="outline" className="text-xs">
                    <Var>{supplierDisplay}</Var>
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs">
                  <Var>{divisionDisplay}</Var>
                </Badge>
                {shipType && (
                  <Badge variant="outline" className="text-xs">
                    <T context="label prefix for ship type code">Ship:</T>{' '}
                    <Var>{shipType}</Var>
                  </Badge>
                )}
                {rebateType && (
                  <Badge variant="outline" className="text-xs">
                    <T context="label prefix for rebate type code">Rebate:</T>{' '}
                    <Var>{rebateType}</Var>
                  </Badge>
                )}
              </>
            );
          })()}
        </div>
        <div className="flex items-center gap-2">
          {onStageInternal && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onStageInternal(transaction)}
              disabled={isActionPending || isPostedTx}
              title={postedFreezeTitle}
            >
              <T context="button to stage a ship-and-debit transaction for internal processing">
                Stage internal
              </T>
            </Button>
          )}
          {onAddToWorkbench && (
            <Button
              size="sm"
              onClick={() => onAddToWorkbench(transaction)}
              disabled={isActionPending || isPostedTx}
              title={postedFreezeTitle}
            >
              <T context="verb, move S&D transactions into the staging queue for processing">
                Stage
              </T>
            </Button>
          )}
        </div>
      </div>

      {/* ── Body: left content + right activity ─────────────────── */}
      <div className="flex gap-4 flex-1 min-h-0 pt-3">
        {/* Left column */}
        <div className="flex-1 min-w-0 overflow-y-auto pr-1 space-y-4">
          {/* Hero metric cards */}
          <div className="grid grid-cols-4 gap-3">
            <Card
              className={
                isNegativeClaim ? 'bg-red-50 border-red-200' : 'bg-muted/40'
              }
            >
              <CardContent className="p-3">
                <p className="text-[11px] text-muted-foreground mb-1">
                  <T context="label for the claim amount metric card">
                    Claim Amount
                  </T>
                </p>
                <p
                  className={`text-lg font-semibold tabular-nums ${isNegativeClaim ? 'text-red-700' : ''}`}
                >
                  {formatAccountingCurrency(claimAmount)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-muted/40">
              <CardContent className="p-3">
                <p className="text-[11px] text-muted-foreground mb-1">
                  <T context="label for the quoted price metric card">
                    Quoted Price
                  </T>
                </p>
                <p className="text-lg font-semibold tabular-nums">
                  {formatAccountingCurrency(quotedPrice, 4)}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  <T context="label prefix for wholesale price in metric card">
                    Wholesale:
                  </T>{' '}
                  {formatAccountingCurrency(wholesalePrice, 4)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-muted/40">
              <CardContent className="p-3">
                <p className="text-[11px] text-muted-foreground mb-1">
                  <T context="label for the last purchase order cost metric card">
                    Last PO Cost
                  </T>
                </p>
                <p className="text-lg font-semibold tabular-nums">
                  {formatAccountingCurrency(landedCost, 4)}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  <T context="label prefix for SPA cost in metric card">SPA:</T>{' '}
                  {formatAccountingCurrency(spaCost, 4)}
                </p>
              </CardContent>
            </Card>
            <Card
              className={
                isNegativeQty ? 'bg-red-50 border-red-200' : 'bg-muted/40'
              }
            >
              <CardContent className="p-3">
                <p className="text-[11px] text-muted-foreground mb-1">
                  <T context="label for quantity metric card, may include unit of measure in parentheses">
                    Quantity
                  </T>
                  {quantityUom ? ` (${quantityUom})` : ''}
                </p>
                <p
                  className={`text-lg font-semibold tabular-nums ${isNegativeQty ? 'text-red-700' : ''}`}
                >
                  {formatNumber(quantity)}
                </p>
                {isNegativeQty && (
                  <p className="text-[10px] text-red-600 font-medium">
                    <T context="warning label shown when a transaction has a negative quantity">
                      Negative quantity
                    </T>
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Key dates */}
          <div className="grid grid-cols-3 gap-3">
            <div className="flex items-center gap-3 rounded-md border px-3 py-2">
              <div>
                <p className="text-[10px] text-muted-foreground">
                  <T context="label for the order date field">Order Date</T>
                </p>
                <p className="text-sm font-semibold">
                  {/* eslint-disable-next-line i18next/no-literal-string -- formatted date value from transaction data, not a translatable string */}
                  <Var>{fmtDate(tx(transaction, 'order_date'), 'long')}</Var>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-md border px-3 py-2">
              <div>
                <p className="text-[10px] text-muted-foreground">
                  <T context="label for the ship date field">Ship Date</T>
                </p>
                <p className="text-sm font-semibold">
                  {/* eslint-disable-next-line i18next/no-literal-string -- formatted date value from transaction data, not a translatable string */}
                  <Var>{fmtDate(tx(transaction, 'ship_date'), 'long')}</Var>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-md border px-3 py-2">
              <div>
                <p className="text-[10px] text-muted-foreground">
                  <T context="label for the invoice date field">Invoice Date</T>
                </p>
                <p className="text-sm font-semibold">
                  {/* eslint-disable-next-line i18next/no-literal-string -- formatted date value from transaction data, not a translatable string */}
                  <Var>{fmtDate(tx(transaction, 'invoice_date'), 'long')}</Var>
                </p>
              </div>
            </div>
          </div>

          {/* Invoice lines table */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-muted-foreground">
                <T context="section heading for the invoice lines table">
                  Invoice Lines
                </T>
                {invoiceLines.length > 0 && (
                  <span className="ml-1 text-muted-foreground/60">
                    ({invoiceLines.length})
                  </span>
                )}
              </p>
              {invoiceLines.length > INVOICE_PAGE_SIZE && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>
                    {invoicePage * INVOICE_PAGE_SIZE + 1}&ndash;
                    {Math.min(
                      (invoicePage + 1) * INVOICE_PAGE_SIZE,
                      invoiceLines.length,
                    )}{' '}
                    <T context="pagination: separator between current range and total count">
                      of
                    </T>{' '}
                    {invoiceLines.length}
                  </span>
                  <button
                    type="button"
                    className="px-1.5 py-0.5 rounded hover:bg-muted disabled:opacity-30"
                    disabled={invoicePage === 0}
                    onClick={() => setInvoicePage((p) => p - 1)}
                  >
                    &larr;
                  </button>
                  <button
                    type="button"
                    className="px-1.5 py-0.5 rounded hover:bg-muted disabled:opacity-30"
                    disabled={
                      (invoicePage + 1) * INVOICE_PAGE_SIZE >=
                      invoiceLines.length
                    }
                    onClick={() => setInvoicePage((p) => p + 1)}
                  >
                    &rarr;
                  </button>
                </div>
              )}
            </div>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">
                      <T context="table column header for invoice number">
                        Invoice #
                      </T>
                    </TableHead>
                    <TableHead className="text-xs">
                      <T context="table column header for purchase order number">
                        PO #
                      </T>
                    </TableHead>
                    <TableHead className="text-xs">
                      <T context="table column header for supplier">Supplier</T>
                    </TableHead>
                    <TableHead className="text-xs">
                      <T context="table column header for division code (abbreviated)">
                        Div
                      </T>
                    </TableHead>
                    <TableHead className="text-xs text-right">
                      <T context="table column header for invoice amount">
                        Amount
                      </T>
                    </TableHead>
                    <TableHead className="text-xs text-right">
                      <T context="table column header for cost">Cost</T>
                    </TableHead>
                    <TableHead className="text-xs text-right">
                      <T context="table column header for purchase order cost">
                        PO Cost
                      </T>
                    </TableHead>
                    <TableHead className="text-xs text-right">
                      <T context="table column header for quantity">Qty</T>
                    </TableHead>
                    <TableHead className="text-xs">
                      <T context="table column header for invoice date">Date</T>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingDetails ? (
                    <TableRow>
                      {Array.from({ length: 9 }).map((_, i) => (
                        <TableCell key={i}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ) : invoiceLines.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        className="text-center text-xs text-muted-foreground py-4"
                      >
                        <T context="empty state message when no invoice lines exist for the transaction">
                          No invoice lines found
                        </T>
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoiceLines
                      .slice(
                        invoicePage * INVOICE_PAGE_SIZE,
                        (invoicePage + 1) * INVOICE_PAGE_SIZE,
                      )
                      .map((line, i) => (
                        <TableRow key={i} className="hover:bg-muted/30">
                          <TableCell className="text-xs font-mono">
                            {String(line.invoiceNumber ?? '')}
                          </TableCell>
                          <TableCell className="text-xs">
                            {String(line.poNumber ?? '')}
                          </TableCell>
                          <TableCell className="text-xs">
                            {String(line.supplierNumber ?? '')}
                          </TableCell>
                          <TableCell className="text-xs font-mono">
                            {String(line.division ?? '')}
                            {line.location ? ` · ${String(line.location)}` : ''}
                          </TableCell>
                          <TableCell className="text-xs text-right tabular-nums">
                            {line.amount != null
                              ? formatAccountingCurrency(Number(line.amount))
                              : '—'}
                          </TableCell>
                          <TableCell className="text-xs text-right tabular-nums">
                            {line.cost != null
                              ? formatAccountingCurrency(Number(line.cost))
                              : '—'}
                          </TableCell>
                          <TableCell className="text-xs text-right tabular-nums">
                            {line.poCost != null
                              ? formatAccountingCurrency(Number(line.poCost))
                              : '—'}
                          </TableCell>
                          <TableCell className="text-xs text-right tabular-nums">
                            {line.quantity != null
                              ? formatNumber(Number(line.quantity))
                              : '—'}
                          </TableCell>
                          <TableCell className="text-xs">
                            {fmtInvDate(line.invoiceDate)}
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Tabs */}
          <div>
            <div className="flex gap-1 border-b mb-3">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-foreground text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                  <span className="ml-1 text-[10px] text-muted-foreground">
                    {tab.key}
                  </span>
                </button>
              ))}
            </div>

            <div className="min-h-[180px]">
              {/* Tab 1 — Item */}
              {activeTab === 'item' && (
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs">
                  <KV
                    label={gt('Item #', {
                      context:
                        'label for item number field in transaction detail',
                    })}
                    value={txStr(transaction, 'item_with_prefix')}
                  />
                  <KV
                    label={gt('Supplier Code', {
                      context:
                        'label for supplier code field in transaction detail',
                    })}
                    value={txStr(transaction, 'vendor_product_no')}
                  />
                  <KV
                    label={gt('Description', {
                      context:
                        'label for item description field in transaction detail',
                    })}
                    value={(itemMaster?.englishDescription as string) ?? '—'}
                  />
                  <KV
                    label={gt('Core Cost', {
                      context:
                        'label for core cost field in transaction detail',
                    })}
                    value={
                      tx(transaction, 'core_cost_override') != null
                        ? formatAccountingCurrency(
                            Number(tx(transaction, 'core_cost_override')),
                            4,
                          )
                        : '—'
                    }
                  />
                  <KV
                    label={gt('Stocking UOM', {
                      context: 'label for stocking unit of measure field',
                    })}
                    value={txStr(transaction, 'uom')}
                  />
                  <KV
                    label={gt('Buyer Cost', {
                      context:
                        'label for buyer cost field in transaction detail',
                    })}
                    value={
                      tx(transaction, 'unit_price_wholesale') != null
                        ? formatAccountingCurrency(
                            Number(tx(transaction, 'unit_price_wholesale')),
                            4,
                          )
                        : '—'
                    }
                  />
                  <KV
                    label={gt('UPC MFG', {
                      context: 'label for UPC manufacturer qualifier field',
                    })}
                    value={txStr(transaction, 'upc_qualifier')}
                  />
                  <KV
                    label={gt('EDI Supplier', {
                      context: 'label for EDI supplier flag field',
                    })}
                    value={
                      tx(transaction, 'edi_supplier_flag') === 'Y'
                        ? gt('Yes', { context: 'EDI supplier flag is enabled' })
                        : gt('No', { context: 'EDI supplier flag is disabled' })
                    }
                  />
                  <KV
                    label={gt('UPC Item', {
                      context: 'label for UPC item code field',
                    })}
                    value={txStr(transaction, 'upc_code')}
                  />
                </div>
              )}

              {/* Tab 2 — Customer */}
              {activeTab === 'customer' && (
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs">
                  <KV
                    label={gt('Customer Name', {
                      context:
                        'label for customer name field in transaction detail',
                    })}
                    value={txStr(transaction, 'customer_name')}
                  />
                  <KV
                    label={gt('Name/City', {
                      context: 'label for customer name and city field',
                    })}
                    value={txStr(transaction, 'customer_name_city')}
                  />
                  <KV
                    label={gt('Customer #', {
                      context: 'label for customer number field',
                    })}
                    value={txStr(transaction, 'sold_to_no')}
                  />
                  <KV
                    label={gt('Company', {
                      context: 'label for company field in transaction detail',
                    })}
                    value={
                      locationLookup.companyName
                        ? `${locationLookup.companyName} (${companyCode})`
                        : companyCode
                    }
                  />
                  <KV
                    label={gt('Ship-to #', {
                      context: 'label for ship-to number field',
                    })}
                    value={txStr(transaction, 'ship_to_no')}
                  />
                  <KV
                    label={gt('Division', {
                      context: 'label for division field in transaction detail',
                    })}
                    value={
                      locationLookup.divisionName
                        ? `${locationLookup.divisionName} (${divisionCode})`
                        : divisionCode
                    }
                  />
                  <KV
                    label={gt('Sold-to #', {
                      context: 'label for sold-to number field',
                    })}
                    value={txStr(transaction, 'sold_to_no')}
                  />
                  <KV
                    label={gt('Branch', {
                      context: 'label for branch field in transaction detail',
                    })}
                    value={
                      locationLookup.branchName
                        ? `${locationLookup.branchName} (${txStr(transaction, 'selling_location')})`
                        : txStr(transaction, 'selling_location')
                    }
                  />
                  <KV
                    label={gt('Location', {
                      context:
                        'label for customer location (city, state, postal code) field',
                    })}
                    value={[
                      txStr(transaction, 'customer_city'),
                      txStr(transaction, 'customer_state'),
                      txStr(transaction, 'customer_postal_code'),
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  />
                  <KV
                    label={gt('DUNS Loc', {
                      context: 'label for DUNS location code field',
                    })}
                    value={txStr(transaction, 'duns_gl_branch')}
                  />
                  {dunsMismatch && (
                    <div className="col-span-2">
                      <SpaDunsWarning mismatch={dunsMismatch} />
                    </div>
                  )}
                </div>
              )}

              {/* Tab 3 — Pricing */}
              {activeTab === 'pricing' && (
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs">
                  <KV
                    label={gt('Claim Amount', {
                      context: 'label for claim amount field in pricing tab',
                    })}
                    value={formatAccountingCurrency(claimAmount)}
                    valueClassName={
                      isNegativeClaim ? 'text-red-600' : undefined
                    }
                  />
                  <KV
                    label={gt('SPA Cost', {
                      context:
                        'label for SPA (special pricing agreement) cost field',
                    })}
                    value={formatAccountingCurrency(spaCost, 4)}
                  />
                  <KV
                    label={gt('Quoted Price', {
                      context: 'label for quoted price field in pricing tab',
                    })}
                    value={formatAccountingCurrency(quotedPrice, 4)}
                  />
                  <KV
                    label={gt('Contract #', {
                      context: 'label for contract number field in pricing tab',
                    })}
                    value={txStr(transaction, 'supplier_contract_no')}
                  />
                  <KV
                    label={gt('Wholesale Price', {
                      context: 'label for wholesale price field in pricing tab',
                    })}
                    value={formatAccountingCurrency(wholesalePrice, 4)}
                  />
                  <KV
                    label={gt('Agreement #', {
                      context:
                        'label for agreement number field in pricing tab',
                    })}
                    value={
                      ((
                        fullTransactionData?.e844d as Record<string, unknown>
                      )?.['agreement_no'] as string) ?? '—'
                    }
                  />
                  <KV
                    label={gt('Last PO Cost', {
                      context:
                        'label for last purchase order cost field in pricing tab',
                    })}
                    value={formatAccountingCurrency(landedCost, 4)}
                  />
                  <KV
                    label={gt('Order Time Override Core', {
                      context:
                        'label for order time override core agreement cost field',
                    })}
                    value={
                      ((
                        fullTransactionData?.e844sdRaw as
                          Record<string, unknown> | undefined
                      )?.['order_time_override_core_agreement_cost'] as
                        number | null) != null
                        ? formatAccountingCurrency(
                            (
                              fullTransactionData?.e844sdRaw as Record<
                                string,
                                unknown
                              >
                            )[
                              'order_time_override_core_agreement_cost'
                            ] as number,
                            4,
                          )
                        : '—'
                    }
                  />
                  <KV
                    label={gt('Invoice Time Override Core', {
                      context:
                        'label for invoice time override core agreement cost field',
                    })}
                    value={
                      ((
                        fullTransactionData?.e844sdRaw as
                          Record<string, unknown> | undefined
                      )?.['invoice_time_override_core_agreement_cost'] as
                        number | null) != null
                        ? formatAccountingCurrency(
                            (
                              fullTransactionData?.e844sdRaw as Record<
                                string,
                                unknown
                              >
                            )[
                              'invoice_time_override_core_agreement_cost'
                            ] as number,
                            4,
                          )
                        : '—'
                    }
                  />
                  {((): Array<{ label: string; key: string }> => [
                    {
                      label: gt('Inv Time Branch Buyer Cost', {
                        context:
                          'label for invoice time branch buyer cost field',
                      }),
                      key: BUYER_COST_FIELD_KEYS[0],
                    },
                    {
                      label: gt('Invoice Time Buyer Cost', {
                        context: 'label for invoice time buyer cost field',
                      }),
                      key: BUYER_COST_FIELD_KEYS[1],
                    },
                    {
                      label: gt('Order Time Branch Buyer Cost', {
                        context: 'label for order time branch buyer cost field',
                      }),
                      key: BUYER_COST_FIELD_KEYS[2],
                    },
                    {
                      label: gt('Order Time Buyer Cost', {
                        context: 'label for order time buyer cost field',
                      }),
                      key: BUYER_COST_FIELD_KEYS[3],
                    },
                  ])().map(({ label, key }) => (
                    <KV
                      key={key}
                      label={label}
                      value={
                        ((
                          fullTransactionData?.e844sdRaw as
                            Record<string, unknown> | undefined
                        )?.[key] as number | null) != null
                          ? formatAccountingCurrency(
                              (
                                fullTransactionData?.e844sdRaw as Record<
                                  string,
                                  unknown
                                >
                              )[key] as number,
                              4,
                            )
                          : '—'
                      }
                    />
                  ))}
                  <KV
                    label={gt('Batch Ctrl #', {
                      context:
                        'label for batch control number field in pricing tab',
                    })}
                    value={txStr(transaction, 'extract_control_no')}
                  />
                </div>
              )}

              {/* Tab 4 — Raw data */}
              {activeTab === 'raw' && (
                <div>
                  <p className="text-[10px] text-muted-foreground mb-2">
                    <T context="subtitle for raw data tab showing the data source and field count">
                      Imported from AS400 &middot; <Var>{rawFields.length}</Var>{' '}
                      fields
                    </T>
                  </p>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs font-mono">
                    {(showAllRaw ? rawFields : rawFields.slice(0, 12)).map(
                      ([key, val]) => (
                        <div
                          key={key}
                          className="flex justify-between gap-2 py-0.5"
                        >
                          <span className="text-muted-foreground truncate">
                            {key}
                          </span>
                          <span className="text-right truncate max-w-[200px]">
                            {String(val)}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                  {rawFields.length > 12 && (
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground mt-2"
                      onClick={() => setShowAllRaw(!showAllRaw)}
                    >
                      {showAllRaw ? (
                        <T context="button to collapse raw data fields list">
                          Show less
                        </T>
                      ) : (
                        <T context="button to expand raw data fields list, shows count of fields">
                          Show all <Var>{rawFields.length}</Var> fields
                        </T>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right column — Activity log */}
        <div className="w-[280px] shrink-0 flex flex-col border-l pl-4">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            <T context="heading for the activity log panel">Activity</T>
            {activities.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-[10px]">
                {activities.length}
              </Badge>
            )}
          </p>
          <ScrollArea className="flex-1 min-h-0 mb-3">
            {isLoadingDetails ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : activities.length > 0 ? (
              <div className="space-y-1 pr-3">
                {activities.map((a) => {
                  const label =
                    ACTIVITY_LABELS[a.activityType] ??
                    a.activityType.replace(/_/g, ' ');
                  const ts = new Date(a.eventAt);
                  const timeStr = isNaN(ts.getTime())
                    ? a.eventAt
                    : format(ts, ACTIVITY_TIMESTAMP_FORMAT);
                  const badgeColor =
                    ACTIVITY_BADGE_COLORS[a.activityType] ??
                    DEFAULT_BADGE_COLOR;
                  const isComment = a.activityType === 'comment_added';
                  const isOwnComment =
                    isComment && a.userId === currentUser?.id;
                  const isWithinHour =
                    isComment &&
                    Date.now() - new Date(a.createdAt).getTime() <
                      60 * 60 * 1000;
                  const canEditComment =
                    isOwnComment && isWithinHour && !!onEditComment;
                  const canDeleteComment =
                    isOwnComment && isWithinHour && !!onDeleteComment;
                  const isEditingThis = editingCommentId === a.id;
                  const isEdited = isComment && !!a.metadata?.editedAt;
                  return (
                    <div
                      key={a.id}
                      className="group py-1.5 border-b border-border/40 last:border-0"
                    >
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badgeColor}`}
                        >
                          {label}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          <Var>
                            {a.user?.name ??
                              gt('System', {
                                context:
                                  'placeholder name for system-generated activity log entries',
                              })}
                          </Var>
                        </span>
                        {(canEditComment || canDeleteComment) &&
                          !isEditingThis && (
                            <span className="ml-auto flex gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                              {canEditComment && (
                                <button
                                  type="button"
                                  aria-label={gt('Edit comment', {
                                    context:
                                      'aria label for button to edit a comment in the activity log',
                                  })}
                                  title={gt('Edit comment', {
                                    context:
                                      'tooltip for button to edit a comment in the activity log',
                                  })}
                                  className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                                  onClick={() => {
                                    setEditingCommentId(a.id);
                                    setEditingCommentText(a.description);
                                  }}
                                >
                                  <PencilSimpleIcon className="h-3 w-3" />
                                </button>
                              )}
                              {canDeleteComment && (
                                <button
                                  type="button"
                                  aria-label={gt('Delete comment', {
                                    context:
                                      'aria label for button to delete a comment in the activity log',
                                  })}
                                  title={gt('Delete comment', {
                                    context:
                                      'tooltip for button to delete a comment in the activity log',
                                  })}
                                  className="p-0.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive disabled:opacity-50"
                                  disabled={isDeletePending}
                                  onClick={() => onDeleteComment(a.id)}
                                >
                                  <TrashIcon className="h-3 w-3" />
                                </button>
                              )}
                            </span>
                          )}
                      </div>
                      {isEditingThis ? (
                        <div className="mt-1 space-y-1">
                          <Textarea
                            value={editingCommentText}
                            onChange={(e) =>
                              setEditingCommentText(e.target.value)
                            }
                            rows={2}
                            className="text-[11px] resize-none"
                            autoFocus
                          />
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-5 px-1.5 text-[10px]"
                              onClick={async () => {
                                if (!onEditComment) return;
                                await onEditComment(
                                  a.id,
                                  editingCommentText.trim(),
                                );
                                setEditingCommentId(null);
                              }}
                              disabled={!editingCommentText.trim()}
                            >
                              <CheckIcon className="h-3 w-3 mr-0.5" />
                              <T context="button to save an edited comment">
                                Save
                              </T>
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-5 px-1.5 text-[10px]"
                              onClick={() => setEditingCommentId(null)}
                            >
                              <T context="button to cancel editing a comment">
                                Cancel
                              </T>
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
                          {a.description}
                        </p>
                      )}
                      <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                        {timeStr}
                        {isEdited && (
                          <span className="italic">
                            <T context="indicator shown on activity log entries that have been edited">
                              {' '}
                              (edited)
                            </T>
                          </span>
                        )}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                <T context="empty state message shown when there are no activity log entries">
                  No activity yet.
                </T>
              </p>
            )}
          </ScrollArea>
          <div className="space-y-2 pt-2 border-t">
            <Textarea
              ref={commentRef}
              placeholder={gt('Add a comment...', {
                context:
                  'placeholder text for the comment input field in activity log',
              })}
              className="text-xs min-h-[60px] resize-none"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  handleSubmitComment();
                }
              }}
            />
            <Button
              size="sm"
              className="w-full"
              disabled={!commentText.trim()}
              onClick={handleSubmitComment}
            >
              <T context="button to submit a comment in the activity log">
                Add comment
              </T>
            </Button>
          </div>
        </div>
      </div>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between pt-3 mt-3 border-t text-[10px] text-muted-foreground">
        <div className="flex items-center gap-3">
          <span>
            <kbd className="px-1 py-0.5 rounded bg-muted font-mono">J</kbd>{' '}
            <T context="keyboard shortcut hint for navigating to next transaction">
              next
            </T>
          </span>
          <span>
            <kbd className="px-1 py-0.5 rounded bg-muted font-mono">K</kbd>{' '}
            <T context="keyboard shortcut hint for navigating to previous transaction">
              prev
            </T>
          </span>
          <span>
            <kbd className="px-1 py-0.5 rounded bg-muted font-mono">S</kbd>{' '}
            <T context="keyboard shortcut hint for staging a transaction">
              stage
            </T>
          </span>
          <span>
            <kbd className="px-1 py-0.5 rounded bg-muted font-mono">C</kbd>{' '}
            <T context="keyboard shortcut hint for focusing the comment field">
              comment
            </T>
          </span>
          <span>
            <kbd className="px-1 py-0.5 rounded bg-muted font-mono">1-4</kbd>{' '}
            <T context="keyboard shortcut hint for switching between tabs">
              tabs
            </T>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span>
            <T context="footer label showing selling location and shipping location codes for the transaction">
              Sell/Ship loc <Var>{txStr(transaction, 'selling_location')}</Var>{' '}
              &middot; <Var>{txStr(transaction, 'shipping_location')}</Var>
            </T>
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Key/Value row ──────────────────────────────────────────────────────

function KV({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span
        className={`font-medium text-right truncate ${valueClassName ?? ''}`}
      >
        {value || '—'}
      </span>
    </div>
  );
}
